import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { useFleet, useHasPermission } from '../hooks';
import { DeviceHealth } from '../interfaces';
import { DevicesService, OTAService } from '../services';

export const useDeviceOTAv2 = (id: string, enabled = true) => {
  const fleet = useFleet();
  const queryKey = ['devices', 'otav2', id, fleet.id];

  const queryFn = () => DevicesService.getOTAv2(id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && enabled,
  });
};

export const useOTADevice = (device: DeviceHealth, enabled = true) => {
  const fleet = useFleet();

  const queryFn = () => OTAService.getOTAConfiguration(device.installed_ota_id, true);
  const queryKey = device.installed_ota_id
    ? ['devices', 'otav2', device.installed_ota_id, fleet.id]
    : ['devices', 'otav2', device.id, fleet.id];

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && enabled,
  });
};

export const useOTAv2 = (configId: number, enabled = true) => {
  const fleet = useFleet();
  const queryKey = ['devices', 'otav2', configId, fleet.id];

  const queryFn = () => OTAService.getOTAConfiguration(configId);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id && !!configId && enabled,
  });
};

export const useOTARepository = () => {
  const fleet = useFleet();
  const queryKey = ['devices', 'otaRepository', fleet.id];

  const queryFn = () => DevicesService.getOTARepo();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
  });
};

export const useOTAConfigurations = () => {
  const fleet = useFleet();
  const queryKey = ['ota', 'configurations', fleet.id];

  const queryFn = () => OTAService.getOTAConfigurations();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
  });
};

export const useOTAGroups = () => {
  const fleet = useFleet();
  const queryKey = ['ota', 'groups', fleet.id];

  const queryFn = () => OTAService.getGroups();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
    defaultData: [],
  });
};

export const useOTAGroupStats = () => {
  const fleet = useFleet();
  const queryKey = ['ota', 'groupStats', fleet.id];

  const queryFn = () => OTAService.getGroupStats();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
  });
};

export const useOTAGroupsById = (selectedFleetId) => {
  const queryKey = ['ota', 'groups', selectedFleetId];

  const queryFn = () => OTAService.getGroupsByFleetId(selectedFleetId);

  const hasPermission = useHasPermission('ota:read:list');

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!selectedFleetId && hasPermission,
  });
};

export const useOTAFilesQuery = () => {
  const fleet = useFleet();
  const queryKey = ['ota', 'files', fleet.id];

  const queryFn = () => OTAService.getOTAFiles();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
  });
};

export const useOTAFileCategories = () => {
  const fleet = useFleet();
  const queryKey = ['ota', 'file-categories', fleet.id];

  const queryFn = () => OTAService.getOTAFileCategories();

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
  });
};

export const refreshOTAFiles = (fleetId: number) => {
  const queryKey = ['ota', 'files', fleetId];

  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(queryKey);
};

export const refreshOTAGroups = (fleetId: number) => {
  const queryKey = ['ota', 'groups', fleetId];

  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(queryKey);
};

export const refreshOTAConfigurations = (id: number) => {
  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['ota', 'configurations', id]);
};

export const refreshDeviceOTA = (id: string) => {
  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['devices', 'ota', id]);
};

export const refreshOTAv2 = (configId: string) => {
  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['devices', 'otav2', configId]);
};

export const refreshOTAfileCategories = () => {
  queryClient.invalidateQueries(['ota', 'file-categories']);
};
